"use client"

import { useState, useSyncExternalStore } from "react"
import type { CaseFilm } from "./film-config"
import { pickRungIndex } from "./film-quality"

type NavigatorWithConnection = Navigator & {
  connection?: { saveData?: boolean; effectiveType?: string; downlink?: number }
}

/**
 * The player inside the film dialog: poster first, captions on, a transcript link, and an error state.
 * Quality: starts from the screen size and the connection, steps down one rung when a file fails to load,
 * and only reports an error when the lowest rung fails too.
 */
const subscribeNothing = () => () => {}

export function FilmPlayer({ film }: { film: CaseFilm }) {
  const last = film.rungs.length - 1
  // The first rung comes from the browser (screen, connection); the server and the first paint assume 720p.
  const picked = useSyncExternalStore(
    subscribeNothing,
    () =>
      pickRungIndex(
        {
          viewportWidth: window.innerWidth,
          devicePixelRatio: window.devicePixelRatio || 1,
          connection: (navigator as NavigatorWithConnection).connection,
        },
        film.rungs,
      ),
    () => Math.min(1, last),
  )
  const [stepsDown, setStepsDown] = useState(0)
  const [failed, setFailed] = useState(false)
  const rung = Math.min(picked + stepsDown, last)

  const height = film.rungs[rung] ?? 360
  const src = `${film.videoBase}/${film.filePattern.replace("{height}", String(height))}`

  return (
    <div className="player">
      <video
        key={src}
        className="scr"
        controls
        preload="none"
        playsInline
        poster={film.poster}
        src={src}
        onError={() => {
          if (rung < last) setStepsDown((n) => n + 1)
          else setFailed(true)
        }}
        aria-label="One real crash, start to finish"
        data-quality={`${height}p`}
      >
        <track kind="captions" src={film.captions.src} srcLang={film.captions.srcLang} label={film.captions.label} default />
        <track kind="chapters" src={film.chapters} srcLang="en" label="Chapters" default />
      </video>
      {failed ? (
        <p role="alert" className="small">
          The film could not be loaded. You can read the transcript instead.
        </p>
      ) : null}
      <p className="small">
        <a className="ln" href={film.transcriptHref} target="_blank" rel="noopener noreferrer">
          Read the transcript<span className="vh"> (opens in a new tab)</span>
        </a>
      </p>
    </div>
  )
}
